// @ts-check
/** Capture a walk-in lead at the rail: someone who tried a piece but did not buy. Name and phone are the
 * only required fields, so it takes seconds. */
import { useEffect, useState } from 'react';
import { useToast } from '../context/ToastContext';
import adminPosOrderService from '../services/adminPosOrderService';
import adminWalkInLeadService from '../services/adminWalkInLeadService';
import { countryByIso, countryForPhone } from '../utils/countries';
import { hasMeasurements } from '../utils/customerMeasurements';
import { productByName, sizeOptions } from '../utils/posCatalog';
import { buildLeadPayload, validateLeadForm } from '../utils/walkInLead';
import CountrySelect from './CountrySelect';
import MeasurementInputs from './MeasurementInputs';
import PosProductPicker from './PosProductPicker';
import Modal from './ui/Modal';
import TemplateActions from './whatsapp/TemplateActions';

const input = 'w-full px-3 py-3 text-base border border-input rounded-lg';

const DEFAULT_COUNTRY = 'IN';

/**
 * @param {{salesEventId: string, eventCountry?: string | null, lead?: any, onClose: () => void, onSaved?: () => void}} props
 * eventCountry (ISO-2) preselects the phone country; India when the event has none.
 * lead, when given, prefills the form and saves to that lead (edit) instead of creating a new one.
 * The stored phone is E.164, so an edit selects the country its dial code identifies, else the event's.
 */
const WalkInLeadModal = ({ salesEventId, eventCountry, lead, onClose, onSaved }) => {
  const toast = useToast();
  const [form, setForm] = useState(() => {
    const fallbackCountry = countryByIso(eventCountry)?.iso ?? DEFAULT_COUNTRY;
    return lead
      ? {
          name: lead.name,
          phone: lead.phone,
          phoneCountry: countryForPhone(lead.phone, fallbackCountry)?.iso ?? fallbackCountry,
          style: lead.style || '',
          sizeTried: lead.size_tried || '',
          notes: lead.notes || '',
          measurements: lead.measurements || {},
          fitNotes: lead.fit_notes || '',
          preferredProductId: lead.preferred_product_id || null,
        }
      : {
          name: '',
          phone: '',
          phoneCountry: fallbackCountry,
          style: '',
          sizeTried: '',
          notes: '',
          measurements: {},
          fitNotes: '',
          preferredProductId: null,
        };
  });
  const [saving, setSaving] = useState(false);
  // The style/size text fields become dropdowns once the catalogue is available - falls back to
  // free text (never blocking capture) if it fails to load, e.g. on flaky venue Wi-Fi.
  const [catalog, setCatalog] = useState(/** @type {any[]} */ ([]));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { products } = await adminPosOrderService.getCatalog();
        if (!cancelled) setCatalog(products);
      } catch {
        // Free-text style/size fields are the fallback - never block capturing a lead on this.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const selectedProduct = productByName(catalog, form.style);
  const preferredProduct = form.preferredProductId
    ? (catalog.find((p) => p.product_id === form.preferredProductId) ?? null)
    : null;
  const styleChoices = catalog.map((p) => p.name);
  if (form.style && !styleChoices.includes(form.style)) styleChoices.unshift(form.style);
  const sizeChoices = selectedProduct ? sizeOptions(selectedProduct) : [];
  if (form.sizeTried && !sizeChoices.includes(form.sizeTried)) sizeChoices.unshift(form.sizeTried);

  const handleSave = async () => {
    const problem = validateLeadForm(form);
    if (problem) {
      toast.error(problem);
      return;
    }
    setSaving(true);
    try {
      if (lead) {
        const { sales_event_id: _omit, ...patch } = buildLeadPayload(form, salesEventId);
        await adminWalkInLeadService.update(lead.id, patch);
        toast.success('Lead updated');
      } else {
        await adminWalkInLeadService.create(buildLeadPayload(form, salesEventId));
        toast.success('Lead saved');
      }
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err.message || 'Failed to save the lead');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal maxWidth="sm" onClose={onClose} hasUnsavedChanges={Boolean(form.name || form.phone)}>
      <div className="px-6 py-4 border-b">
        <h2 className="text-lg font-semibold">{lead ? 'Edit lead' : 'Capture a lead'}</h2>
      </div>
      <div className="px-6 py-4 space-y-3">
        <input
          className={input}
          placeholder="Name *"
          value={form.name}
          onChange={(e) => set({ name: e.target.value })}
        />
        <div className="flex gap-2">
          <CountrySelect
            variant="phone"
            ariaLabel="Phone country"
            value={form.phoneCountry}
            onChange={(phoneCountry) => set({ phoneCountry })}
          />
          <input
            className={`${input} min-w-0`}
            type="tel"
            inputMode="tel"
            placeholder="WhatsApp number *"
            value={form.phone}
            onChange={(e) => set({ phone: e.target.value })}
          />
        </div>
        {catalog.length > 0 ? (
          <select
            aria-label="Style tried"
            className={input}
            value={form.style}
            onChange={(e) => set({ style: e.target.value, sizeTried: '' })}
          >
            <option value="">Style tried</option>
            {styleChoices.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        ) : (
          <input
            className={input}
            placeholder="Style tried"
            value={form.style}
            onChange={(e) => set({ style: e.target.value })}
          />
        )}
        {catalog.length > 0 ? (
          <select
            aria-label="Size tried"
            className={input}
            value={form.sizeTried}
            onChange={(e) => set({ sizeTried: e.target.value })}
            disabled={!selectedProduct && !form.sizeTried}
          >
            <option value="">{selectedProduct || form.sizeTried ? 'Size tried' : 'Choose a style first'}</option>
            {sizeChoices.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        ) : (
          <input
            className={input}
            placeholder="Size tried"
            value={form.sizeTried}
            onChange={(e) => set({ sizeTried: e.target.value })}
          />
        )}
        <div>
          <label className="block mb-1 text-sm text-foreground/80">Preferred outfit</label>
          {form.preferredProductId ? (
            <div className="flex items-center justify-between gap-2 p-2 border border-input rounded-lg">
              <span className="text-sm">{preferredProduct?.name || 'Selected outfit'}</span>
              <button
                type="button"
                onClick={() => set({ preferredProductId: null })}
                className="text-sm text-red-700 hover:underline"
              >
                Clear
              </button>
            </div>
          ) : (
            <PosProductPicker
              products={catalog}
              onPick={(product) => set({ preferredProductId: product.product_id })}
            />
          )}
        </div>
        <details className="text-sm">
          <summary className="cursor-pointer text-muted-foreground">
            Measurements{' '}
            {hasMeasurements(form) && (
              <span className="ml-1 px-2 py-0.5 rounded-md bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300 text-xs">
                Measured
              </span>
            )}
          </summary>
          <div className="mt-2">
            <MeasurementInputs measurements={form.measurements} fitNotes={form.fitNotes} onChange={set} />
          </div>
        </details>
        <textarea
          className={input}
          rows={2}
          placeholder="Notes"
          value={form.notes}
          onChange={(e) => set({ notes: e.target.value })}
        />
      </div>
      {lead && <TemplateActions scope="leads" id={lead.id} className="px-6 pb-4" />}
      <div className="flex gap-2 px-6 py-4 border-t">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex-1 px-4 py-3 text-base bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:bg-muted-foreground/50"
        >
          {saving ? 'Saving...' : lead ? 'Save changes' : 'Save lead'}
        </button>
        <button
          onClick={onClose}
          disabled={saving}
          className="px-4 py-3 text-base border border-input rounded-lg hover:bg-accent"
        >
          Cancel
        </button>
      </div>
    </Modal>
  );
};

export default WalkInLeadModal;
